import CachedIcon from "@mui/icons-material/Cached";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import { Box } from "@mui/material";
import { styled } from "@mui/system";
import { Icon, Refresh, UploadThumbnail } from "@platformx/shared/static-assets";
import { Fragment, useEffect, useState } from "react";
import { DamContentGallery } from "./components/damContentGallery/DamContentGallery";
import ManualCropUsingCdn from "./components/ManualCropUsingCdn";
import { formThumbnailFromGCBURL } from "./utils/cdnFlow";
import "./x-image-render.css";

interface XImageRenderCdnProps {
  callBack: (obj?: any, name?: string) => void;
  editData: any;
  isCrop?: boolean;
  name?: string;
  prelemId?: string;
  prelemIndex?: number;
  aspect_ratio?: string;
  isColorPallete?: boolean;
  handleRefresh?: () => void;
  handleColorPallete?: (color: string) => void;
  containerSize?: { width: string; height: string };
}

interface OriginalImagePayload {
  Thumbnail: string;
  Title: string;
  Description: string;
  bitStreamId: string;
  original_image_relative_path: string;
  ext: string;
}

interface SelectedAssetPayload {
  original_image: OriginalImagePayload;
  published_images?: any[];
}

const RoundBox = styled("div")({
  position: "absolute",
  right: "20px",
  top: "20px",
  width: "40px",
  height: "40px",
  background: "#fff",
  zIndex: "9",
  borderRadius: "4px",
  boxShadow: "0 10px 25px 0 rgba(0, 0, 0, 0.12)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
});

const XImageRenderCdn = ({
  callBack,
  editData,
  isCrop = true,
  name = "",
  prelemId = "",
  aspect_ratio = "",
  isColorPallete = false,
  handleRefresh = () => {},
  handleColorPallete = () => {},
  containerSize = { width: "100%", height: "192px" },
}: XImageRenderCdnProps) => {
  const [selectedAssetPayload, setSelectedAssetPayload] = useState<SelectedAssetPayload>(editData);
  const processing = false;
  const [manualCropShow, setManualCropShow] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [isImage, setIsImage] = useState(editData.isImg || false);
  const colorCode = [
    "#b29a53",
    "#ba8b78",
    "#ae6958",
    "#d86057",
    "#b75c8d",
    "#68669a",
    "#5c98ba",
    "#334075",
    "#246d73",
    "#806a71",
    "#514146",
  ];
  const previewThumbnailUrl = selectedAssetPayload?.original_image?.original_image_relative_path
    ? formThumbnailFromGCBURL(
        selectedAssetPayload.original_image.original_image_relative_path,
        selectedAssetPayload?.original_image?.ext,
        "quality=85,format=webp",
      )
    : "";

  const handleSelectedImage = (selectedAsset) => {
    const nextSelectedAssetPayload = {
      original_image: selectedAsset || {},
      published_images: [],
    };
    setSelectedAssetPayload(nextSelectedAssetPayload);
    setIsImage(true);
    if (name) {
      callBack(nextSelectedAssetPayload, name);
    } else {
      callBack(nextSelectedAssetPayload);
    }

    toggleGallery(false, "done");
  };

  const toggleGallery = (toggleState: boolean, _type: string) => {
    setGalleryDialogOpen(toggleState);
  };

  const showGallery = () => {
    window.scrollTo(0, 0);
    setGalleryDialogOpen(true);
  };

  const onUploadClick = () => {
    if (!isImage) setIsImage(true);
    showGallery();
  };

  const onColorPaletteSelect = (color: string) => {
    handleColorPallete?.(color);
  };

  const onRefreshClick = () => {
    handleRefresh?.();
  };

  const backTo = () => {
    if (manualCropShow) {
      setManualCropShow(false);
    }
  };

  const changeCrop = () => {
    setManualCropShow(true);
  };

  const doneCropCompleted = (cropHints: any[] = []) => {
    const nextSelectedAssetPayload = {
      ...selectedAssetPayload,
      published_images: cropHints,
    };

    setSelectedAssetPayload(nextSelectedAssetPayload);
    setManualCropShow(false);

    if (name) {
      callBack(nextSelectedAssetPayload, name);
    } else {
      callBack(nextSelectedAssetPayload);
    }
  };

  const renderReplaceOverlay = () => (
    <Box
      sx={{
        position: "absolute",
        top: "0",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#7470708a",
        borderRadius: "15px",
      }}>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ cursor: "pointer" }} onClick={onUploadClick}>
          <Box
            sx={{
              borderRadius: "50%",
              backgroundColor: "#fff",
              width: "25px",
              height: "25px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "auto",
            }}>
            <CachedIcon sx={{ color: "#626060" }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderImagePreview = () => {
    if (previewThumbnailUrl) {
      return (
        <Box
          sx={{
            position: "relative",
            borderRadius: "15px",
            height: containerSize?.height,
            width: containerSize?.width,
            "& picture": {
              height: containerSize?.height || "192px",
            },
          }}
          mb={2}>
          {previewThumbnailUrl && isCrop && (
            <RoundBox onClick={changeCrop}>
              <ViewQuiltIcon
                sx={{
                  fontSize: { md: "22px" },
                }}
              />
            </RoundBox>
          )}
          <img
            style={{
              width: "100%",
              height: containerSize?.height,
              objectFit: "cover",
              display: "flex",
              borderRadius: "15px",
            }}
            src={previewThumbnailUrl}
            alt='socialshare1'
          />
          {renderReplaceOverlay()}
        </Box>
      );
    }

    return (
      <Box
        className='x-image-render'
        data-testid='image-render'
        sx={{
          height: containerSize?.height,
          width: containerSize?.width,
          borderRadius: "15px",
          cursor: "pointer",
          backgroundColor: "#EFF0F6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
        onClick={onUploadClick}>
        <Box
          sx={{
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          m={1}>
          <img width='40px' height='40px' src={UploadThumbnail} alt='UploadIcon' />
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    setSelectedAssetPayload(editData);
    setIsImage(editData?.isImg || Boolean(editData?.original_image?.original_image_relative_path));
  }, [editData]);

  return (
    <Fragment>
      {isColorPallete && editData.colorCode && !isImage ? (
        <Box
          sx={{
            width: "100%",
            height: "192px",
            aspectRatio: {
              xs: "4 / 3",
              sm: "4 / 3",
              md: "1 / 1",
              em: "4 / 3",
              lg: "16 / 9",
              xl: "3 / 1",
            },
            backgroundColor: editData.colorCode,
            borderRadius: "15px",
          }}></Box>
      ) : (
        <>
          <Box
            sx={{
              backgroundColor: "#FFF",
            }}>
            {galleryDialogOpen && (
              <DamContentGallery
                handleImageSelected={handleSelectedImage}
                toggleGallery={toggleGallery}
                assetType={"Image"}
                processing={processing}
                dialogOpen={galleryDialogOpen}
                isCrop={isCrop}
              />
            )}
          </Box>
          {renderImagePreview()}
        </>
      )}
      {isColorPallete && (
        <Box
          sx={{
            marginTop: "10px",
            display: "flex",
            flexDirection: "row",
            flexFlow: { xs: "wrap", lg: "nowrap" },
          }}>
          <Box
            onClick={onUploadClick}
            sx={{
              width: "27px",
              height: "27px",
              flexGrow: "0",
              borderRadius: "20px",
              backgroundColor: "#fff",
              margin: {
                xs: "0px 8px 8px 0px",
                lg: "0px 8px 8px 0px",
              },
              border: "solid 1px #2d2d39",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}>
            <img src={Icon} alt='Icon' />
          </Box>
          {colorCode.map((val) => {
            return (
              <Box
                key={val}
                onClick={() => onColorPaletteSelect(val)}
                sx={{
                  width: "27px",
                  height: "27px",
                  flexGrow: "0",
                  borderRadius: "20px",
                  backgroundColor: val,
                  margin: {
                    xs: "0px 8px 8px 0px",
                    lg: "0px 8px 8px 0px",
                  },
                  border: val === "#fff" ? "solid 1px #e6eaed" : null,
                  cursor: "pointer",
                }}></Box>
            );
          })}
          {editData.colorCode !== "" || previewThumbnailUrl ? (
            <Box onClick={onRefreshClick} className='refershEnabled'>
              <img src={Refresh} alt='Refresh' />
            </Box>
          ) : (
            <Box className='refershDisabled'>
              <img src={Refresh} alt='Refresh' />
            </Box>
          )}
        </Box>
      )}
      {manualCropShow && (
        <ManualCropUsingCdn
          open={manualCropShow}
          backTo={backTo}
          doneCropCompleted={doneCropCompleted}
          originalImage={selectedAssetPayload?.original_image}
          isCrop={isCrop}
          published_images={selectedAssetPayload?.published_images}
          aspect_ratio={aspect_ratio}
          prelemId={prelemId}
        />
      )}
    </Fragment>
  );
};

export default XImageRenderCdn;
